import {
    Button,
    Dialog,
    DialogContent,
    DialogTrigger,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@repo/shadcn';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { ChevronDownIcon, Loader2Icon, Trash } from 'lucide-react';
import { toast } from 'sonner';

import { ButtonGroup } from '@components/ui/button';

import {
    useDeleteBrandMutation,
    useEditBrandMutation,
} from '@/features/slice/products/brand/brand-api-slice';
import { toastError } from '@/utils';

import { BrandForm } from '../brand-form.component';

const ActionCell = ({ row }: CellContext<any, unknown>) => {
    const [deleteBrand, { isLoading: isDeleting }] = useDeleteBrandMutation();
    const [editBrand, { isLoading: isEditing }] = useEditBrandMutation();

    const handleDeleteBrand = async (brandId: string) => {
        const confirmed = window.confirm(
            'Are you sure you want to delete this brand?',
        );
        if (!confirmed) return;

        try {
            await deleteBrand(brandId).unwrap();
            toast.success('Brand has been deleted');
        } catch (error) {
            toastError(
                error,
                'useBrandList: handleDeleteBrand -> Failed to delete brand.',
            );
        }
    };

    const handleEdit = async (data: any) => {
        try {
            await editBrand({
                id: row.original.id,
                name: data.name,
                description: data.description,
            }).unwrap();
            toast.success('Brand has been updated');
        } catch (error) {
            toastError(error, 'Failed to edit brand.');
        }
    };

    return (
        <div className={'flex justify-end'}>
            <ButtonGroup>
                <Dialog>
                    <DialogTrigger>
                        <Button type={'button'} variant={'outline'}>
                            Edit
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <BrandForm
                            initialValues={{
                                name: row.original.name,
                                description: row.original.description,
                            }}
                            onSubmit={handleEdit}
                            title="Edit Brand"
                            isLoading={isEditing}
                        />
                    </DialogContent>
                </Dialog>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="!pl-2">
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="[--radius:1rem]"
                    >
                        <DropdownMenuGroup>
                            <DropdownMenuItem
                                className={'flex gap-2 text-destructive'}
                                onClick={() =>
                                    handleDeleteBrand(row.original.id)
                                }
                            >
                                {isDeleting ? (
                                    <Loader2Icon className={'animate-spin '} />
                                ) : (
                                    <>
                                        <Trash className={'size-4'} />
                                        Delete
                                    </>
                                )}
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </ButtonGroup>
        </div>
    );
};

export const brandListColumns: ColumnDef<any>[] = [
    {
        accessorKey: 'name',
        header: 'Brand Name',
        cell: ({ row }) => {
            return (
                <div
                    title={row.getValue('name')}
                    // className=" w-[200px] cursor-pointer truncate"
                >
                    {row.getValue('name')}
                </div>
            );
        },
    },

    {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => {
            return (
                <div
                    title={row.getValue('description')}
                    // className=" w-[200px] cursor-pointer truncate"
                >
                    {row.getValue('description')}
                </div>
            );
        },
    },

    {
        accessorKey: 'actions',
        header: () => <div className="text-right">Actions</div>,
        cell: ActionCell,
    },
];
